import { Suspense, type ComponentProps } from 'react';
import {
  DescriptionList,
  DescriptionListDetails,
  DescriptionListTerm,
  FormattedRelativeTime,
  Icon,
  InlineStack,
  Label,
  Link,
  Panel,
  PanelActions,
  PanelHeader,
  PanelSection,
  PanelTitle,
  PlainLink,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import type { Delivery } from '@/services/db/delivery/entity';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import type { Subject, SubjectAction, SubjectDetail } from '@/services/db/subject/entity';
import { useSubject } from '@/services/db/subject/queries';
import { useProductTaxonomy, type ProductTaxonomy } from '@/services/product-taxonomy';
import { generatePath } from '@/services/router/generate-path';
import { actionTypeIcon, ActionTypeLabels } from '@/utils/campaign-labels';
import { isEmptyString } from '@/utils/string';

type LabelColor = ComponentProps<typeof Label>['color'];

/** Subject-state → { label, color } (Ember `getStateLabels`). Unknown states render a neutral placeholder. */
const STATE_LABELS: Record<string, { label: string; color: LabelColor }> = {
  init: { label: 'Starting', color: 'green' },
  active: { label: 'Active', color: 'green' },
  inactive: { label: 'Leaving', color: 'grey' },
  finished: { label: 'Finished', color: 'outline' },
  exited_early: { label: 'Exited Early', color: 'yellow' },
};

const DEFAULT_STATE_LABEL = { label: '…', color: 'outline' } as const satisfies { label: string; color: LabelColor };

type DeliveryJourneysPanelProps = { delivery: Delivery };

/**
 * The Journeys panel for a delivery that belongs to a journey subject. FK-gated
 * on `subject_id` and wrapped in an error boundary: a deleted/inaccessible
 * subject degrades to nothing rather than crashing the detail page.
 */
export function DeliveryJourneysPanel({ delivery }: DeliveryJourneysPanelProps) {
  const subjectId = delivery.subject_id;
  if (subjectId == null || isEmptyString(subjectId)) {
    return null;
  }
  return (
    <AppErrorBoundary fallback={null} resetKeys={[delivery.id]}>
      <Suspense fallback={null}>
        <JourneysPanelContent delivery={delivery} subjectId={subjectId} />
      </Suspense>
    </AppErrorBoundary>
  );
}

type JourneysPanelContentProps = { delivery: Delivery; subjectId: string };

function JourneysPanelContent({ delivery, subjectId }: JourneysPanelContentProps) {
  const { data } = useSubject({ id: subjectId });
  const taxonomy = useProductTaxonomy();
  const href = journeyHref(delivery, data);

  return (
    <Panel mt="200">
      <PanelHeader>
        <PanelTitle>Journeys</PanelTitle>
        {href != null ? (
          <PanelActions>
            <Link href={href} variant="secondary" isUnderlined={false} icon="chevron-right" iconPosition="trailing">
              <Text size="xs" color="inherit">
                View full journey
              </Text>
            </Link>
          </PanelActions>
        ) : null}
      </PanelHeader>
      <PanelSection>
        <DescriptionList>
          <DescriptionListTerm>Started</DescriptionListTerm>
          <DescriptionListDetails>
            <StartedCell subject={data.subject} href={href} />
          </DescriptionListDetails>

          <DescriptionListTerm>Status</DescriptionListTerm>
          <DescriptionListDetails>
            <JourneyStatusLabel subject={data.subject} />
          </DescriptionListDetails>

          <DescriptionListTerm>Next Step</DescriptionListTerm>
          <DescriptionListDetails>
            <NextStep subject={data.subject} nextAction={nextActionOf(data)} taxonomy={taxonomy} />
          </DescriptionListDetails>
        </DescriptionList>
      </PanelSection>
    </Panel>
  );
}

type StartedCellProps = { subject: Subject; href: string | null };

function StartedCell({ subject, href }: StartedCellProps) {
  if ((subject.match_time ?? 0) <= 0) {
    return (
      <Text color="subtle" data-test-journey-started>
        —
      </Text>
    );
  }
  const started = (
    <FormattedRelativeTime
      date={subject.match_time}
      absoluteFormatDiffCutoff={null}
      withTooltip
      data-test-journey-started
    />
  );
  return href != null ? <PlainLink href={href}>{started}</PlainLink> : started;
}

type JourneyStatusLabelProps = { subject: Subject };

function JourneyStatusLabel({ subject }: JourneyStatusLabelProps) {
  const state = STATE_LABELS[subject.subject_state] ?? DEFAULT_STATE_LABEL;
  return (
    <Label color={state.color} data-test-journey-label>
      {state.label}
    </Label>
  );
}

type NextStepProps = { subject: Subject; nextAction: SubjectAction | undefined; taxonomy: ProductTaxonomy };

function NextStep({ subject, nextAction, taxonomy }: NextStepProps) {
  if (subject.subject_state !== 'active') {
    return <Text color="subtle">—</Text>;
  }
  return (
    <InlineStack gap="050" alignBlock="center">
      {nextAction != null ? (
        <Tooltip content={ActionTypeLabels[nextAction.type] ?? nextAction.name}>
          <Icon name={actionTypeIcon(nextAction.type)} data-test-next-action-icon />
        </Tooltip>
      ) : (
        <Tooltip content={`Leave ${taxonomy.term('campaigns.singular')}`}>
          <Icon name="exit" data-test-next-action-exit />
        </Tooltip>
      )}
      {(subject.next_wake_up ?? 0) > 0 ? (
        <FormattedRelativeTime date={subject.next_wake_up} absoluteFormatDiffCutoff={null} withTooltip />
      ) : (
        <Text color="subtle">—</Text>
      )}
    </InlineStack>
  );
}

/** The subject's next action, resolved from the sideloaded actions by `next_action_id`. */
function nextActionOf({ subject, actions }: SubjectDetail): SubjectAction | undefined {
  if (isEmptyString(subject.next_action_id)) {
    return undefined;
  }
  return actions.find((a) => a.id === subject.next_action_id);
}

/**
 * The "view full journey" URL: a webhook-triggered journey links to the
 * campaign's subject; every other journey links to the person's subject
 * (Ember `journeyLink`). Returns `null` when the target id is absent.
 */
function journeyHref(delivery: Delivery, { subject, campaigns }: SubjectDetail): string | null {
  const workspaceId = requireWorkspaceId();
  const subjectId = subject.id;
  const campaign = campaigns.find((c) => c.id === subject.campaign_id);

  if (campaign?.type === 'webhook') {
    if (delivery.campaign_id == null) {
      return null;
    }
    return generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/journeys/:subjectId', {
      workspaceId,
      campaignId: delivery.campaign_id,
      subjectId,
    });
  }

  const personId = delivery.customer_id;
  if (personId == null || isEmptyString(personId)) {
    return null;
  }
  return generatePath('/workspaces/:workspaceId/journeys/people/:personId/journeys/:subjectId', {
    workspaceId,
    personId,
    subjectId,
  });
}
